/**
 * Tutorial.tsx — R15 Builder-AV first-run tutorial overlay
 *
 * 4-page swipeable/clickable tutorial shown on first MainMenu load.
 * localStorage key `gdg.tutorial.shown === "true"` gates repeat display.
 *
 * Pages:
 *   1. Welcome — game intro + Mae greeting + 4 archetype chips
 *   2. Turn flow — roll → walk → NPC interact → end turn
 *   3. Tile types — 8 tile-type chips with 1-sentence each
 *   4. Win conditions — 4 endings + pollution threshold + round limit
 *
 * VISIBLE WHEN:
 *   localStorage["gdg.tutorial.shown"] !== "true"
 *   AND store.gamePhase === "menu"
 *   (MainMenu renders <Tutorial> when those conditions hold)
 *   Tutorial.tsx itself does NOT re-read the store — parent gates the render.
 *
 * TEST PATH:
 *   1. Open game in a fresh browser (no localStorage)
 *   2. MainMenu loads → Tutorial overlay appears above MainMenu (z:2400)
 *   3. Click Next 3× → "Begin Adventure" button on page 4 → overlay closes
 *   4. localStorage["gdg.tutorial.shown"] === "true"
 *   5. Reload → Tutorial does NOT appear
 *   6. Open PauseMenu → How to Play → "Replay Tutorial" → Tutorial appears
 *
 * z-index: 2400 — above MainMenu (2000) and CutscenePlayer (2200).
 * A11y: ARIA dialog, focus-trapped, Esc = Skip.
 */

import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "../../i18n/useI18n.js";
import styles from "./Tutorial.module.css";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const TUTORIAL_STORAGE_KEY = "gdg.tutorial.shown";

export const TUTORIAL_PAGE_COUNT = 4;

// Stable string keys for page-dot list (avoids index-as-key biome lint).
const TUTORIAL_DOT_KEYS: readonly string[] = ["dot-a", "dot-b", "dot-c", "dot-d"];

/** Read/write the "tutorial already shown" flag. */
export function getTutorialShown(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(TUTORIAL_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export function setTutorialShown(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(TUTORIAL_STORAGE_KEY, "true");
  } catch {
    // ignore — private mode / blocked storage
  }
}

// ---------------------------------------------------------------------------
// Archetype data (page 1)
// ---------------------------------------------------------------------------

const ARCHETYPES = [
  {
    id: "engineer",
    icon: "⚙️",
    labelKey: "tutorial.archetypeEngineer",
    hintKey: "tutorial.archetypeEngineerHint",
  },
  {
    id: "scientist",
    icon: "🔬",
    labelKey: "tutorial.archetypeScientist",
    hintKey: "tutorial.archetypeScientistHint",
  },
  {
    id: "activist",
    icon: "📣",
    labelKey: "tutorial.archetypeActivist",
    hintKey: "tutorial.archetypeActivistHint",
  },
  {
    id: "politician",
    icon: "🏛️",
    labelKey: "tutorial.archetypePolitician",
    hintKey: "tutorial.archetypePoliticianHint",
  },
] as const;

// ---------------------------------------------------------------------------
// Tile-type data (page 3)
// ---------------------------------------------------------------------------

const TILE_TYPES = [
  {
    id: "terrain",
    icon: "🌿",
    labelKey: "tutorial.tileTerrain",
    hintKey: "tutorial.tileTerrainHint",
    actionsKey: "tutorial.tileTerrainActions",
  },
  {
    id: "policy",
    icon: "📜",
    labelKey: "tutorial.tilePolicy",
    hintKey: "tutorial.tilePolicyHint",
    actionsKey: "tutorial.tilePolicyActions",
  },
  {
    id: "event",
    icon: "⚡",
    labelKey: "tutorial.tileEvent",
    hintKey: "tutorial.tileEventHint",
    actionsKey: "tutorial.tileEventActions",
  },
  {
    id: "jail",
    icon: "🔒",
    labelKey: "tutorial.tileJail",
    hintKey: "tutorial.tileJailHint",
    actionsKey: "tutorial.tileJailActions",
  },
  {
    id: "trading",
    icon: "💱",
    labelKey: "tutorial.tileTrading",
    hintKey: "tutorial.tileTradingHint",
    actionsKey: "tutorial.tileTradingActions",
  },
  {
    id: "teleport",
    icon: "🌀",
    labelKey: "tutorial.tileTeleport",
    hintKey: "tutorial.tileTeleportHint",
    actionsKey: "tutorial.tileTeleportActions",
  },
  {
    id: "start",
    icon: "🏁",
    labelKey: "tutorial.tileStart",
    hintKey: "tutorial.tileStartHint",
    actionsKey: "tutorial.tileStartActions",
  },
  {
    id: "coast",
    icon: "🌊",
    labelKey: "tutorial.tileCoast",
    hintKey: "tutorial.tileCoastHint",
    actionsKey: "tutorial.tileCoastActions",
  },
] as const;

// ---------------------------------------------------------------------------
// Ending data (page 4)
// ---------------------------------------------------------------------------

const ENDINGS = [
  {
    id: "ending-a",
    tag: "A",
    color: "#5bc07a",
    labelKey: "tutorial.endingA",
    hintKey: "tutorial.endingAHint",
  },
  {
    id: "ending-b",
    tag: "B",
    color: "#c9974a",
    labelKey: "tutorial.endingB",
    hintKey: "tutorial.endingBHint",
  },
  {
    id: "ending-c",
    tag: "C",
    color: "#e07070",
    labelKey: "tutorial.endingC",
    hintKey: "tutorial.endingCHint",
  },
  {
    id: "ending-d",
    tag: "D",
    color: "#a0b8e0",
    labelKey: "tutorial.endingD",
    hintKey: "tutorial.endingDHint",
  },
] as const;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface TutorialProps {
  /** Called when tutorial is dismissed (Skip or Begin Adventure). */
  onDismiss: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Tutorial({ onDismiss }: TutorialProps): React.ReactElement {
  const [page, setPage] = useState(0); // 0-indexed
  const { t } = useI18n();
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const firstFocusRef = useRef<HTMLButtonElement | null>(null);

  const handleDismiss = useCallback(() => {
    setTutorialShown();
    onDismiss();
  }, [onDismiss]);

  // Focus trap + initial focus — re-fires only on `page` change (intentional).
  // biome-ignore lint/correctness/useExhaustiveDependencies: firstFocusRef is a ref, not reactive; we only want to re-focus on page transition.
  useEffect(() => {
    firstFocusRef.current?.focus();
  }, [page]);

  // Esc = Skip (marks shown, dismisses). Capture phase so we intercept before
  // PauseMenu's own Esc handler (which is also capture-phase but added later).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        handleDismiss();
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
    };
  }, [handleDismiss]);

  const goNext = useCallback(() => {
    if (page < TUTORIAL_PAGE_COUNT - 1) {
      setPage((p) => p + 1);
    } else {
      handleDismiss();
    }
  }, [page, handleDismiss]);

  const goPrev = useCallback(() => {
    if (page > 0) setPage((p) => p - 1);
  }, [page]);

  // Focus trap: keep Tab inside the overlay
  function onKeyDownOverlay(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "Tab") return;
    const overlay = overlayRef.current;
    if (!overlay) return;
    const focusable = Array.from(
      overlay.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => !el.hasAttribute("disabled"));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
  }

  const isLastPage = page === TUTORIAL_PAGE_COUNT - 1;

  return (
    <div
      ref={overlayRef}
      className={styles.overlay}
      data-testid="tutorial-overlay"
      // biome-ignore lint/a11y/useSemanticElements: <dialog> resets z-index
      // stacking context; div+role=dialog is established pattern in this
      // codebase (PauseMenu/Minimap/GameOverModal all use the same).
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutorial-title"
      onKeyDown={onKeyDownOverlay}
    >
      <div className={styles.panel} data-testid="tutorial-panel">
        {/* ── Header: Skip always visible ── */}
        <div className={styles.header}>
          <span
            className={styles.pageIndicatorLabel}
            aria-label={`Page ${page + 1} of ${TUTORIAL_PAGE_COUNT}`}
          >
            {page + 1} / {TUTORIAL_PAGE_COUNT}
          </span>
          <button
            type="button"
            className={styles.skipBtn}
            onClick={handleDismiss}
            data-testid="tutorial-skip"
            aria-label={t("tutorial.skip")}
          >
            {t("tutorial.skip")}
          </button>
        </div>

        {/* ── Page dots — static fixed-length list, index is canonical id ── */}
        <div className={styles.dots} aria-hidden="true" data-testid="tutorial-dots">
          {TUTORIAL_DOT_KEYS.map((dotKey, i) => (
            <span
              key={dotKey}
              className={`${styles.dot}${i === page ? ` ${styles.dotActive}` : ""}`}
              data-testid={`tutorial-dot-${i}`}
            />
          ))}
        </div>

        {/* ── Page content ── */}
        <div className={styles.content} data-testid="tutorial-content">
          {page === 0 && <PageWelcome t={t} />}
          {page === 1 && <PageTurnFlow t={t} />}
          {page === 2 && <PageTileTypes t={t} />}
          {page === 3 && <PageWinConditions t={t} />}
        </div>

        {/* ── Navigation row ── */}
        <div className={styles.navRow} data-testid="tutorial-nav">
          <button
            type="button"
            className={styles.navBtn}
            onClick={goPrev}
            disabled={page === 0}
            data-testid="tutorial-prev"
            aria-label={t("tutorial.prev")}
          >
            {t("tutorial.prev")}
          </button>

          {/* Page indicator dots (screen-reader skip row) */}
          <div className={styles.navSpacer} aria-hidden="true" />

          {isLastPage ? (
            <button
              ref={firstFocusRef}
              type="button"
              className={`${styles.navBtn} ${styles.navBtnPrimary}`}
              onClick={handleDismiss}
              data-testid="tutorial-begin"
              aria-label={t("tutorial.begin")}
            >
              {t("tutorial.begin")}
            </button>
          ) : (
            <button
              ref={firstFocusRef}
              type="button"
              className={`${styles.navBtn} ${styles.navBtnPrimary}`}
              onClick={goNext}
              data-testid="tutorial-next"
              aria-label={t("tutorial.next")}
            >
              {t("tutorial.next")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page 1 — Welcome
// ---------------------------------------------------------------------------

function PageWelcome({ t }: { t: (key: string) => string }): React.ReactElement {
  return (
    <div data-testid="tutorial-page-welcome">
      <h2 id="tutorial-title" className={styles.pageTitle}>
        {t("tutorial.welcomeTitle")}
      </h2>
      <p className={styles.pageBody}>{t("tutorial.welcomeBody")}</p>
      <p className={styles.maeIntro} data-testid="tutorial-mae-intro">
        {t("tutorial.maeIntro")}
      </p>
      <div className={styles.archetypeGrid} data-testid="tutorial-archetypes">
        {ARCHETYPES.map((a) => (
          <div
            key={a.id}
            className={styles.archetypeChip}
            data-testid={`tutorial-archetype-${a.id}`}
          >
            <span className={styles.archetypeIcon} aria-hidden="true">
              {a.icon}
            </span>
            <span className={styles.archetypeLabel}>{t(a.labelKey)}</span>
            <span className={styles.archetypeHint}>{t(a.hintKey)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page 2 — Turn flow
// ---------------------------------------------------------------------------

function PageTurnFlow({ t }: { t: (key: string) => string }): React.ReactElement {
  return (
    <div data-testid="tutorial-page-turn">
      <h2 id="tutorial-title" className={styles.pageTitle}>
        {t("tutorial.turnTitle")}
      </h2>
      <p className={styles.pageBody}>{t("tutorial.turnBody")}</p>
      <ol className={styles.stepList} data-testid="tutorial-turn-steps">
        <li className={styles.stepItem} data-testid="tutorial-step-roll">
          <span className={styles.stepNum}>1</span>
          <span>{t("tutorial.turnStep1")}</span>
        </li>
        <li className={styles.stepItem} data-testid="tutorial-step-walk">
          <span className={styles.stepNum}>2</span>
          <span>{t("tutorial.turnStep2")}</span>
        </li>
        <li className={styles.stepItem} data-testid="tutorial-step-npc">
          <span className={styles.stepNum}>3</span>
          <span>{t("tutorial.turnStep3")}</span>
        </li>
        <li className={styles.stepItem} data-testid="tutorial-step-end">
          <span className={styles.stepNum}>4</span>
          <span>{t("tutorial.turnStep4")}</span>
        </li>
      </ol>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page 3 — Tile types
// ---------------------------------------------------------------------------

function PageTileTypes({ t }: { t: (key: string) => string }): React.ReactElement {
  return (
    <div data-testid="tutorial-page-tiles">
      <h2 id="tutorial-title" className={styles.pageTitle}>
        {t("tutorial.tilesTitle")}
      </h2>
      <div className={styles.tileGrid} data-testid="tutorial-tile-grid">
        {TILE_TYPES.map((tile) => (
          <div key={tile.id} className={styles.tileChip} data-testid={`tutorial-tile-${tile.id}`}>
            <span className={styles.tileIcon} aria-hidden="true">
              {tile.icon}
            </span>
            <span className={styles.tileLabel}>{t(tile.labelKey)}</span>
            <span className={styles.tileHint}>{t(tile.hintKey)}</span>
            <span className={styles.tileActions} data-testid={`tutorial-tile-${tile.id}-actions`}>
              {t(tile.actionsKey)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page 4 — Win conditions
// ---------------------------------------------------------------------------

function PageWinConditions({ t }: { t: (key: string) => string }): React.ReactElement {
  return (
    <div data-testid="tutorial-page-win">
      <h2 id="tutorial-title" className={styles.pageTitle}>
        {t("tutorial.winTitle")}
      </h2>
      <p className={styles.pageBody}>{t("tutorial.winBody")}</p>
      <div className={styles.endingGrid} data-testid="tutorial-endings">
        {ENDINGS.map((e) => (
          <div
            key={e.id}
            className={styles.endingChip}
            data-testid={`tutorial-ending-${e.id}`}
            style={{ borderColor: e.color } as React.CSSProperties}
          >
            <span className={styles.endingTag} style={{ color: e.color } as React.CSSProperties}>
              {e.tag}
            </span>
            <span className={styles.endingLabel}>{t(e.labelKey)}</span>
            <span className={styles.endingHint}>{t(e.hintKey)}</span>
          </div>
        ))}
      </div>
      <p className={styles.thresholdNote} data-testid="tutorial-threshold-note">
        {t("tutorial.thresholdNote")}
      </p>
    </div>
  );
}
